import { useState, useEffect, useRef } from 'react';
import { daysUntil, daysLabel, daysColorClass, formatDueDate, stepsFromAssignment, nextSchoolDay } from '../lib/underground';
import { toggleStep } from '../lib/supabase';
import roundelGreen from '../assets/roundel-green.svg';

export default function AssignmentCard({ assignment, onUpdated, onEdit, onDelete, onRepeat, isAdmin, isStudent, studentDbId }) {
  // Parents and teachers (isAdmin) may edit and remove anything. A student may
  // edit anything on their own planner but remove only what they added
  // themselves. These are guard rails in the app, not locks: the database
  // already lets a student change their own planner.
  const canEdit = isAdmin || isStudent;
  const canRemove = isAdmin || (isStudent && assignment.added_by_student && assignment.added_by_student === studentDbId);

  const steps = stepsFromAssignment(assignment);
  const completedCount = steps.filter(s => s.done).length;
  const allDone = steps.length > 0 && completedCount === steps.length;
  const progress = steps.length ? Math.round(completedCount / steps.length * 100) : 0;
  const d = daysUntil(assignment.due_date);

  // Animate progress bar from 0 to actual width on mount
  const [animatedProgress, setAnimatedProgress] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAnimatedProgress(progress), 50);
    return () => clearTimeout(t);
  }, [progress]);

  // "Again tomorrow": one tap copies this card to the next school day. The
  // planner does the work and answers 'added', 'exists' or 'failed'. The button
  // stays disabled from the tap until the planner has reloaded, so a double tap
  // makes one copy. On success the list reloads and this card is replaced.
  const [repeating, setRepeating] = useState(false);
  const [repeatMsg, setRepeatMsg] = useState('');
  // Checked synchronously: a second tap that lands before React re-renders the
  // button as disabled must still be refused.
  const repeatLock = useRef(false);
  const nextDay = new Date(nextSchoolDay() + 'T00:00:00');
  const repeatLabel = nextDay.getDay() === 1 ? 'Again Monday' : 'Again tomorrow';
  const repeatWeekday = nextDay.toLocaleDateString('en-GB', { weekday: 'long' });

  async function handleRepeat() {
    if (repeatLock.current) return;
    repeatLock.current = true;
    setRepeating(true);
    setRepeatMsg('');
    const result = await onRepeat(assignment);
    if (result === 'added') return; // the reload replaces this card
    setRepeatMsg(result === 'exists' ? `Already on the map for ${repeatWeekday}` : 'Couldn\u2019t add it \u2014 try again');
    repeatLock.current = false;
    setRepeating(false);
    setTimeout(() => setRepeatMsg(''), 4000);
  }

  async function handleToggle(stepId, currentDone) {
    await toggleStep(stepId, !currentDone);
    onUpdated();
  }

  async function handleDoneButton() {
    if (allDone) {
      if (!window.confirm('Mark all steps as not done?')) return;
      await Promise.all(steps.map(s => toggleStep(s.id, false)));
    } else {
      if (!window.confirm('Mark all steps as done?')) return;
      await Promise.all(steps.filter(s => !s.done).map(s => toggleStep(s.id, true)));
    }
    onUpdated();
  }

  const isUrgent = !allDone && d >= 0 && d <= 2;
  const isOverdue = !allDone && d < 0;

  const tapeBg = isOverdue
    ? 'repeating-linear-gradient(180deg,#7b0000,#7b0000 5px,#a00000 5px,#a00000 10px)'
    : isUrgent
    ? 'repeating-linear-gradient(180deg,#f3a712,#f3a712 5px,#1a1a1a 5px,#1a1a1a 10px)'
    : null;

  return (
    <div className="card" style={{ position: 'relative', borderLeft: tapeBg ? 'none' : `5px solid ${assignment.color}`, paddingLeft: tapeBg ? '19px' : undefined, overflow: 'hidden' }}>
      {/* Left-edge tape strip for urgent/overdue */}
      {tapeBg && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 8,
          bottom: 0,
          background: tapeBg,
          borderRadius: '8px 0 0 8px',
        }}/>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{assignment.name}</div>
          {assignment.subject && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              <span style={{ color: assignment.color, fontWeight: 700 }}>●</span> {assignment.subject}
            </div>
          )}
        </div>
        <span className={`badge ${daysColorClass(d, allDone)}`}>{daysLabel(d, allDone)}</span>
      </div>

      {/* Due date */}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
        📅 Due {formatDueDate(assignment.due_date)}
      </div>

      {/* Steps */}
      {steps.length > 0 && (
        <div className="steps-row">
          {steps.map(step => (
            <span
              key={step.id}
              className={`step-pill ${step.done ? 'done' : 'todo'}`}
              style={{
                borderColor: assignment.color,
                color: step.done ? 'white' : assignment.color,
                background: step.done ? assignment.color : 'transparent'
              }}
              onClick={() => handleToggle(step.id, step.done)}
              title={step.done ? 'Mark undone' : 'Mark done'}
            >
              {step.done ? '✓ ' : ''}{step.label}
            </span>
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div className="progress-bg">
        <div
          className="progress-bar"
          style={{
            width: `${animatedProgress}%`,
            background: assignment.color,
            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {completedCount}/{steps.length} steps done
        </span>
        {canEdit && (
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onEdit}>Edit</button>
            {onRepeat && (
              <button className="btn btn-ghost btn-sm" onClick={handleRepeat} disabled={repeating}>{repeatLabel}</button>
            )}
            {canRemove && (
              <button className="btn btn-sm" style={{ background: 'none', color: 'var(--red)', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }} onClick={onDelete}>Remove</button>
            )}
          </span>
        )}
        <DoneButton allDone={allDone} onClick={handleDoneButton} />
      </div>
      {repeatMsg && (
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginTop: 6 }}>{repeatMsg}</div>
      )}
    </div>
  );
}

function DoneButton({ allDone, onClick }) {
  const [failed, setFailed] = useState(false);
  return (
    <button
      onClick={onClick}
      title={allDone ? 'Mark as not done' : 'Mark all steps as done'}
      style={{
        flexShrink: 0,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        opacity: allDone ? 1 : 0.45,
        transition: 'opacity 0.2s',
      }}
    >
      {failed ? (
        <span style={{ fontSize: 11, fontWeight: 700, color: allDone ? '#00782a' : '#888' }}>
          {allDone ? '✓' : '○'}
        </span>
      ) : (
        <img
          src={roundelGreen}
          alt={allDone ? 'Done' : 'Mark done'}
          width="37"
          height="37"
          style={{ display: 'block' }}
          onError={() => setFailed(true)}
        />
      )}
    </button>
  );
}
